/**
 * Schedule Import API module
 * Handles bulk game creation and tournament associations
 */

import { supabase } from '../lib/supabase';
import { createAPIMethod } from './base';
import { log } from '../utils/logger';
import { tournamentAPI } from './tournaments';

/**
 * Parse a time string to get hours and minutes
 * @param {string} timeStr - Time string (e.g., "10:00 AM", "2:30 PM")
 * @returns {{hours: number, minutes: number}} - Hours and minutes
 */
const parseTime = (timeStr) => {
  if (!timeStr) return { hours: 0, minutes: 0 };

  const cleaned = timeStr.trim().toUpperCase();
  const isPM = cleaned.includes('PM');
  const isAM = cleaned.includes('AM');

  // Remove AM/PM suffix
  const timeOnly = cleaned.replace(/\s*(AM|PM)\s*/i, '');
  const parts = timeOnly.split(':');

  let hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;

  // Convert to 24-hour format
  if (isPM && hours !== 12) {
    hours += 12;
  } else if (isAM && hours === 12) {
    hours = 0;
  }

  return { hours, minutes };
};

/**
 * Combine date and time into ISO string with proper timezone handling
 * @param {Date} date - Date object (the parsed date)
 * @param {string} timeStr - Time string (e.g., "10:00 AM")
 * @param {string} timezone - IANA timezone identifier (e.g., "America/Chicago")
 * @returns {string} - ISO datetime string representing the correct UTC time
 */
const combineDateAndTime = (date, timeStr, timezone = 'America/Chicago') => {
  const { hours, minutes } = parseTime(timeStr);

  // Get the date components
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  const normalizedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

  try {
    // Create the datetime as the user intended it
    const localDateTime = new Date(`${dateStr}T${normalizedTime}:00`);

    // Use Intl to format this date as if it were in the target timezone
    const targetFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Format the local date as if it were in the target timezone
    const targetParts = targetFormatter.formatToParts(localDateTime);
    const targetDateStr = `${targetParts.find(p => p.type === 'year').value}-${targetParts.find(p => p.type === 'month').value}-${targetParts.find(p => p.type === 'day').value}`;
    const targetTimeStr = `${targetParts.find(p => p.type === 'hour').value}:${targetParts.find(p => p.type === 'minute').value}`;

    // If the formatted result matches our input, the interpretation was correct
    if (targetDateStr === dateStr && targetTimeStr === normalizedTime) {
      return localDateTime.toISOString();
    } else {
      // Adjust for the timezone difference
      const targetDT = new Date(`${targetDateStr}T${targetTimeStr}:00`);
      const adjustment = targetDT.getTime() - localDateTime.getTime();
      const corrected = new Date(localDateTime.getTime() - adjustment);
      return corrected.toISOString();
    }
  } catch (err) {
    log.error('Error combining datetime:', err);
    // Fallback to simple local time conversion
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined.toISOString();
  }
};

/**
 * Schedule Import API namespace
 */
export const scheduleImportAPI = {
  /**
   * Import schedule data - creates games and manages tournament associations
   * @param {string} tournamentId - Tournament ID
   * @param {Array} validRows - Array of validated row objects with enriched IDs
   * @param {string} userId - Current user ID for audit trails
   * @param {Object} options - Additional import options
   * @param {Array} options.newDivisions - Array of new divisions to create (from validation)
   * @returns {Promise<{gamesCreated: number, teamsAssociated: number, divisionsLinked: number, divisionsCreated: number, errors: Array}>}
   */
  importSchedule: createAPIMethod(
    async (tournamentId, validRows, userId, options = {}) => {
      log.info(`Starting schedule import: ${validRows.length} rows for tournament ${tournamentId}`);

      const results = {
        gamesCreated: 0,
        teamsAssociated: 0,
        divisionsLinked: 0,
        divisionsCreated: 0,
        createdDivisions: [],
        errors: [],
        createdGameIds: []
      };

      if (!validRows || validRows.length === 0) {
        return results;
      }

      // Step 0: Create new divisions if any
      const { newDivisions = [] } = options;
      let divisionNameToIdMap = {};

      if (newDivisions.length > 0) {
        log.info(`Creating ${newDivisions.length} new divisions before import`);

        try {
          const divisionResult = await tournamentAPI.bulkCreateDivisions(
            tournamentId,
            newDivisions,
            userId
          );

          if (divisionResult.success) {
            divisionNameToIdMap = divisionResult.data.divisionMap || {};
            results.divisionsCreated = divisionResult.data.created?.length || 0;
            results.createdDivisions = divisionResult.data.created || [];
            log.info(`Created ${results.divisionsCreated} new divisions`);
          } else {
            log.error('Failed to create divisions:', divisionResult.error);
            results.errors.push({
              step: 'division_creation',
              error: divisionResult.error?.message || 'Failed to create divisions'
            });
          }
        } catch (err) {
          log.error('Exception creating divisions:', err);
          results.errors.push({
            step: 'division_creation',
            error: err.message
          });
        }

        // Update valid rows with new division IDs
        for (const row of validRows) {
          if (row._isNewDivision && row._divisionName) {
            const divisionId = divisionNameToIdMap[row._divisionName];
            if (divisionId) {
              row._divisionId = divisionId;
            }
          }
        }
      }

      // Fetch tournament timezone before processing games
      const { data: tournament, error: tournamentError } = await supabase
        .from('tournaments')
        .select('timezone')
        .eq('id', tournamentId)
        .single();

      if (tournamentError) {
        log.warn(`Could not fetch tournament timezone: ${tournamentError.message}, using default`);
      }

      const timezone = tournament?.timezone || 'America/Chicago';
      log.info(`Using timezone for game times: ${timezone}`);

      // Step 1: Associate teams with tournament
      const uniqueTeamIds = [...new Set([
        ...validRows.map(r => r._homeTeamId),
        ...validRows.map(r => r._awayTeamId)
      ].filter(Boolean))];

      log.info(`Processing ${uniqueTeamIds.length} unique teams for tournament association`);

      for (const teamId of uniqueTeamIds) {
        try {
          // Check if team already associated
          const { data: existing } = await supabase
            .from('tournament_teams')
            .select('id')
            .eq('tournament_id', tournamentId)
            .eq('team_id', teamId)
            .maybeSingle();

          if (!existing) {
            // Create association
            const { error } = await supabase
              .from('tournament_teams')
              .insert({
                tournament_id: tournamentId,
                team_id: teamId,
                created_by: userId
              });

            if (error) {
              log.warn(`Failed to associate team ${teamId}: ${error.message}`);
            } else {
              results.teamsAssociated++;
            }
          }
        } catch (err) {
          // Likely already exists (409 conflict), which is fine
          if (!err.message?.includes('already exists')) {
            log.warn(`Team association error for ${teamId}: ${err.message}`);
          }
        }
      }

      // Step 2: Associate divisions with tournament
      const uniqueDivisionIds = [...new Set(
        validRows.map(r => r._divisionId).filter(Boolean)
      )];

      log.info(`Processing ${uniqueDivisionIds.length} unique divisions for tournament association`);

      for (const divisionId of uniqueDivisionIds) {
        try {
          // Check if division already associated
          const { data: existing } = await supabase
            .from('tournament_tournament_divisions')
            .select('id')
            .eq('tournament_id', tournamentId)
            .eq('division_id', divisionId)
            .maybeSingle();

          if (!existing) {
            // Create association
            const { error } = await supabase
              .from('tournament_tournament_divisions')
              .insert({
                tournament_id: tournamentId,
                division_id: divisionId,
                created_by: userId
              });

            if (error) {
              log.warn(`Failed to associate division ${divisionId}: ${error.message}`);
            } else {
              results.divisionsLinked++;
            }
          }
        } catch (err) {
          if (!err.message?.includes('already exists')) {
            log.warn(`Division association error for ${divisionId}: ${err.message}`);
          }
        }
      }

      // Step 3: Create games in batches
      const BATCH_SIZE = 50;
      const gameBatches = [];

      for (let i = 0; i < validRows.length; i += BATCH_SIZE) {
        gameBatches.push(validRows.slice(i, i + BATCH_SIZE));
      }

      log.info(`Creating games in ${gameBatches.length} batches`);

      for (const [batchIndex, batch] of gameBatches.entries()) {
        log.info(`Processing batch ${batchIndex + 1} of ${gameBatches.length}`);

        const gameInserts = batch.map(row => {
          const startDateTime = combineDateAndTime(row._parsedDate, row.startTime, timezone);
          const endDateTime = row.endTime
            ? combineDateAndTime(row._parsedDate, row.endTime, timezone)
            : null;

          return {
            tournament_id: tournamentId,
            home_team_id: row._homeTeamId,
            away_team_id: row._awayTeamId,
            location_id: row._locationId,
            tournament_division_id: row._divisionId || null,
            starts_at: startDateTime,
            ends_at: endDateTime,
            timezone: timezone, // Store timezone for display purposes
            status: 'scheduled',
            gamecenter_url: row.gamecenterUrl || null,
            notes: row.notes || null
          };
        });

        try {
          const { data, error } = await supabase
            .from('games')
            .insert(gameInserts)
            .select('id');

          if (error) {
            log.error(`Batch ${batchIndex + 1} failed:`, error);
            results.errors.push({
              batch: batchIndex + 1,
              error: error.message,
              rowCount: batch.length
            });
          } else {
            results.gamesCreated += data.length;
            results.createdGameIds.push(...data.map(g => g.id));
          }
        } catch (err) {
          log.error(`Batch ${batchIndex + 1} exception:`, err);
          results.errors.push({
            batch: batchIndex + 1,
            error: err.message,
            rowCount: batch.length
          });
        }
      }

      log.info(`Import complete: ${results.gamesCreated} games, ${results.teamsAssociated} teams, ${results.divisionsLinked} divisions linked, ${results.divisionsCreated} divisions created`);

      return results;
    },
    { operation: 'Import schedule' }
  ),

  /**
   * Verify import by fetching created games
   * @param {Array<string>} gameIds - Array of game IDs to verify
   * @returns {Promise<Array>} - Array of created game records
   */
  verifyImport: createAPIMethod(
    async (gameIds) => {
      if (!gameIds || gameIds.length === 0) {
        return [];
      }

      const { data, error } = await supabase
        .from('games')
        .select(`
          id,
          starts_at,
          ends_at,
          status,
          home_team:teams!home_team_id (id, name),
          away_team:teams!away_team_id (id, name),
          game_locations (id, name),
          tournament_divisions (id, name)
        `)
        .in('id', gameIds);

      if (error) throw error;
      return data;
    },
    { operation: 'Verify import' }
  ),

  /**
   * Rollback import - delete created games (for error recovery)
   * @param {Array<string>} gameIds - Array of game IDs to delete
   * @returns {Promise<number>} - Number of games deleted
   */
  rollbackImport: createAPIMethod(
    async (gameIds) => {
      if (!gameIds || gameIds.length === 0) {
        return 0;
      }

      const { error, count } = await supabase
        .from('games')
        .delete({ count: 'exact' })
        .in('id', gameIds);

      if (error) throw error;
      return count;
    },
    { operation: 'Rollback import' }
  )
};

export default scheduleImportAPI;
